import axios from 'axios';
import {
  PARTNER_REVIEW_CHANGE_VALUES,
  PARTNER_REVIEW_ORDER_SET,
} from '@shared/store/constants/mutationTypes';

import apiClient from '@shared/lib/general/apiClient';

const actions = {
  changePartnerReviewValues({ commit }, payload) {
    commit({
      type: PARTNER_REVIEW_CHANGE_VALUES,
      changes: payload,
    });
  },
  async getPartnerDetail({ dispatch }, payload) {
    const { corpId } = payload;
    const { data: partner } = await apiClient({
      method: 'GET',
      url: `/corporations/id/${corpId}`,
    });
    const { data } = await apiClient({
      method: 'GET',
      url: `/partner/settings/${corpId}`,
    });
    const partnerSettings = {};
    Object.keys(data).forEach((key) => {
      let value = data[key];
      if (value === 'true') {
        value = true;
      } else if (value === 'false') {
        value = false;
      }
      if (value[0] === '[' || value[0] === '{') {
        value = JSON.parse(data[key]);
      }
      partnerSettings[key] = value;
    });
    dispatch('changePartnerReviewValues', [
      {
        field: 'partner',
        value: partner,
      },
      {
        field: 'partnerSettings',
        value: partnerSettings,
      },
      {
        field: 'loading',
        value: false,
      },
    ]);
  },
  async getPartnerOrderReview({ commit }, payload) {
    const {
      orderNumber,
    } = payload;
    try {
      const { data } = await apiClient({
        method: 'GET',
        url: `/partner/orders/orderNumber/${orderNumber}`,
      });
      if (data && data.orderLines) {
        const paymentPending = data.orderLines.find((ol) => ol.status === 'Payment Pending');

        let invoicePaymentMethod = null;
        if (!paymentPending) {
          const { data: txn } = await apiClient({
            method: 'GET',
            url: `/partner/orders/order/${orderNumber}/payment-method`,
          });
          if (typeof txn.payment_method_details === 'string') {
            invoicePaymentMethod = JSON.parse(txn.payment_method_details);
          }
        }

        commit({
          type: PARTNER_REVIEW_ORDER_SET,
          order: data,
          paymentPending,
          invoicePaymentMethod,
        });
      }
      return !!data;
    } catch (err) {
      console.error(err);
      return false;
    }
  },
  async getPartnerOrderReviewTax({ dispatch, state }) {
    const {
      order: {
        billingAddress,
        orderLines,
      },
    } = state;

    dispatch('changePartnerReviewValues', [{
      field: 'calculatingTax',
      value: true,
    }]);

    const hardwareOrderLine = orderLines.filter((ol) => ol.type.toLowerCase() !== 'subscription');
    const subscriptionOrderLine = orderLines.filter((ol) => ol.type.toLowerCase() === 'subscription');

    let hardwareTax = {};
    if (hardwareOrderLine) {
      const { data: d } = await apiClient({
        method: 'POST',
        url: '/chargebee/corp/estimate/invoice',
        data: {
          billingAddress,
          orderLine: hardwareOrderLine,
        },
      });
      if (d && d.taxes.length > 0) {
        const taxTotal = d.taxes.reduce((acc, item) => acc + item.amount, 0);
        hardwareTax = {
          jurisdiction: billingAddress.state,
          percentage: d.line_items[0].tax_rate,
          taxTotal: taxTotal / 100,
        };
      }
    }

    let subscriptionTax = {};
    if (subscriptionOrderLine.length > 0) {
      const { data: subD } = await apiClient({
        method: 'POST',
        url: '/chargebee/corp/estimate/subscription',
        data: {
          billingAddress,
          orderLine: subscriptionOrderLine,
        },
      });
      if (subD && subD.taxes.length > 0) {
        const taxTotal = subD.taxes.reduce((acc, item) => acc + item.amount, 0);
        subscriptionTax = {
          jurisdiction: billingAddress.state,
          percentage: subD.line_items[0].tax_rate,
          taxTotal: taxTotal / 100,
        };
      }
    }

    dispatch('changePartnerReviewValues', [{
      field: 'hardwareTax',
      value: hardwareTax,
    }, {
      field: 'subscriptionTax',
      value: subscriptionTax,
    }, {
      field: 'calculatingTax',
      value: false,
    }]);
  },
  async getPartnerPaymentMethod({ dispatch, state }) {
    const {
      order: {
        corpId,
      },
    } = state;
    dispatch('changePartnerReviewValues', [{
      field: 'loadingPaymentMethod',
      value: true,
    }]);
    const { data } = await apiClient({
      method: 'GET',
      url: `/chargebee/corporation/${corpId}/payment-method`,
    });
    dispatch('changePartnerReviewValues', [{
      field: 'selectedPaymentMethod',
      value: data,
    }, {
      field: 'loadingPaymentMethod',
      value: false,
    }]);
  },
  async getContractResponse({ dispatch }, payload) {
    try {
      const {
        url,
      } = payload;
      const { data } = await apiClient({
        url: '/partner/settings/doc/response',
        method: 'PUT',
        data: {
          url,
        },
      });
      return data;
    } catch (e) {
      console.error(e);
      dispatch('showAlertToast', {
        message: e,
      });
      return '';
    }
  },
  async addPaymentMethod({ dispatch, state }) {
    const {
      order: {
        billingAddress,
        corporation: {
          id,
          firstName,
          lastName,
          email,
          phone,
        },
      },
      cardToken,
      source,
      paymentMethodId,
      paymentMethod,
    } = state;
    const corporation = {
      id,
      firstName,
      lastName,
      email,
      phone,
      cardToken,
      source,
      paymentMethodId,
      paymentOption: paymentMethod,
      billingAddress: [billingAddress],
    };
    const { data } = await apiClient({
      method: 'POST',
      url: '/chargebee/corporation/payment-method/default',
      data: corporation,
    });
    dispatch('changePartnerReviewValues', [{
      field: 'selectedPaymentMethod',
      value: data,
    }, {
      field: 'showActionModal',
      value: false,
    }]);
  },
  async updatePartnerReviewAddress({ dispatch, state }, payload) {
    const {
      order: {
        corpId,
      },
      order,
    } = state;

    await apiClient({
      method: 'PUT',
      url: `/addresses/corporations/${corpId}/${payload.id}`,
      data: payload,
    });

    const newOrder = { ...order };

    if (payload.type.toLowerCase() === 'billing') {
      newOrder.billingAddress = payload;
    } else if (payload.type.toLowerCase() === 'shipping') {
      const newOrderLines = order.orderLines.map((ol) => {
        const newOl = {
          ...ol,
          shippingAddress: payload,
        };
        return newOl;
      });
      newOrder.orderLines = newOrderLines;
    }
    dispatch('changePartnerReviewValues', [{
      field: 'order',
      value: newOrder,
    }]);
  },
  async completePartnerOrderInvoice({ dispatch, state, getters }) {
    const {
      order: {
        orderNumber,
        corpId,
        corporation,
        orderLines,
        deploymentPeriod,
        agreementOrderId,
      },
      selectedPaymentMethod: {
        id,
      },
    } = state;
    const {
      prTaxTotal,
      prOrderTotal,
    } = getters;

    dispatch('changePartnerReviewValues', [{
      field: 'processingOrder',
      value: true,
    }]);

    const order = {
      orderNumber,
      corpId,
      paymentSourceId: id,
      paymentOption: 'Direct Debit',
      orderTotal: prOrderTotal,
      tax: prTaxTotal,
      deploymentPeriod,
      agreementOrderId,
    };

    try {
      await apiClient({
        method: 'POST',
        url: '/checkout/partner/order/complete',
        data: {
          corporation,
          order,
          orderLine: orderLines,
        },
      });
      dispatch('changePartnerReviewValues', [{
        field: 'processingOrder',
        value: false,
      }]);
    } catch (err) {
      console.error(err.response);
      dispatch('changePartnerReviewValues', [{
        field: 'processingOrder',
        value: false,
      }]);
    }
  },
};

export default actions;
